import { ref, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  GetShopDocument,
  type GetShopQuery,
  ListShopForTransPickupRequestDocument,
  type ListShopForTransPickupRequestQuery,
  type ShopListForTransPickupRequestInput,
  type TransPickupRequestUpdateInput,
} from "@/generated/graphql";
import type {
  DeliveryAvailabilityData,
  DeliveryAreaSelectItem,
  ShopData,
} from "../types";

type ShopListItem = {
  __typename?: "ShopResponse";
  shop_code: string;
  name: string;
};

interface UseDeliveryMoveAddressParams {
  shopSelectValues_from: Ref<{ shop_code: string; name: string }>;
  shopSelectValues_to: Ref<{ shop_code: string; name: string }>;
  shopData_from_address: Ref<string>;
  shopData_from_address1: Ref<string>;
  shopData_to_address: Ref<string>;
  shopData_to_address1: Ref<string>;
  shopData: Ref<ShopData | null>;
  shopFromListItems: Ref<({ __typename?: "ShopResponse"; shop_code: string; name: string } | null)[]>;
  shopToListItems: Ref<({ __typename?: "ShopResponse"; shop_code: string; name: string } | null)[]>;
  shopFromListInput: Ref<ShopListForTransPickupRequestInput>;
  shopToListInput: Ref<ShopListForTransPickupRequestInput>;
  editDeliveryRequest: Ref<TransPickupRequestUpdateInput | null>;
  isDisabledChangeFromTo: Ref<boolean>;
  deliveryAreaName: Ref<string>;
  display_delivery_time: Ref<string>;
  deliveryAvailabilityHeader: Ref<Map<string, string>>;
  deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  disp_deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  listDeliveryArea: Ref<(DeliveryAreaSelectItem | null | undefined)[] | null | undefined>;
  deliveryId_0_Flg: Ref<boolean>;
  deliveryStartHourItem: Ref<number[]>;
  deliveryStartHourValues: Ref<number | null>;
  deliveryStartMinitValues: Ref<{ label: string; deliveryStartMinit: number } | null | undefined>;
  doubleDeliOriconCount: Ref<number | null>;
  charterDeliOriconCount: Ref<number | null>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  getAreaList: (address: string, area_code?: string) => Promise<void>;
}

/**
 * 住所情報の整形や表示用データ生成を提供する composable。
 *
 * @returns 画面ロジックで利用する状態・関数
 */
export const useDeliveryMoveAddress = ({
  shopSelectValues_from,
  shopSelectValues_to,
  shopData_from_address,
  shopData_from_address1,
  shopData_to_address,
  shopData_to_address1,
  shopData,
  shopFromListItems,
  shopToListItems,
  shopFromListInput,
  shopToListInput,
  editDeliveryRequest,
  isDisabledChangeFromTo,
  deliveryAreaName,
  display_delivery_time,
  deliveryAvailabilityHeader,
  deliveryAvailabilityData,
  disp_deliveryAvailabilityData,
  listDeliveryArea,
  deliveryId_0_Flg,
  deliveryStartHourItem,
  deliveryStartHourValues,
  deliveryStartMinitValues,
  doubleDeliOriconCount,
  charterDeliOriconCount,
  errdialog,
  errMessage,
  getAreaList,
}: UseDeliveryMoveAddressParams) => {
  const uniqueByShopCode = (
    list:
      | (ShopListItem | null | undefined)[]
      | null
      | undefined
  ): ShopListItem[] => {
    if (!list) {
      return [];
    }

    const shopCodes = new Set<string>();
    return list.filter((shop): shop is ShopListItem => {
      if (!shop) {
        return false;
      }
      if (shopCodes.has(shop.shop_code)) {
        return false;
      }
      shopCodes.add(shop.shop_code);
      return true;
    });
  };

  /**
   * 配達元選択時 @change
   */
  const onChangeShopFrom = async (): Promise<void> => {
    shopData_from_address.value = "";
    shopData_from_address1.value = "";
    deliveryAreaName.value = "";
    display_delivery_time.value = "";
    deliveryAvailabilityHeader.value = new Map();
    deliveryAvailabilityData.value = [];
    listDeliveryArea.value = [];

    // nullチェックを追加
    if (!shopSelectValues_from.value || shopSelectValues_from.value.shop_code === "") {
      // 配送元がクリアされた場合、配送先の選択肢もクリアする
      shopToListItems.value = [];
      shopSelectValues_to.value = { shop_code: "", name: "" };
      shopData_to_address.value = "";
      shopData_to_address1.value = "";
      isDisabledChangeFromTo.value = true;
      return;
    }

    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      /**
       * 店舗情報取得
       */
      const shop_data = await apolloClient.query<GetShopQuery>({
        query: GetShopDocument,
        variables: {
          chain_code: localStorageGetChainCode(),
          customer_code: localStorageGetCustomerCode(),
          shop_code: shopSelectValues_from.value.shop_code,
        },
      });

      shopData.value = shop_data.data.getShop!;
      shopData_from_address.value =
        "〒" +
        shopData.value.zip_code +
        " " +
        shopData.value.address1 +
        shopData.value.address2 +
        " " +
        (shopData.value.address3 ?? "");

      shopSelectValues_from.value = { shop_code: shopData.value.shop_code, name: shopData.value.name };
      shopToListInput.value.to_shop_code = shopSelectValues_from.value.shop_code;
      shopData_from_address1.value = shopData.value.address1;

      doubleDeliOriconCount.value = shopData.value.double_deli_oricon_count;
      charterDeliOriconCount.value = shopData.value.charter_deli_oricon_count;

      //クリア
      deliveryAvailabilityHeader.value = new Map();
      deliveryAvailabilityData.value = [];
      disp_deliveryAvailabilityData.value = [];
      deliveryId_0_Flg.value = false;
      deliveryStartHourItem.value = [];
      deliveryStartHourValues.value = undefined;
      deliveryStartMinitValues.value = undefined;
      listDeliveryArea.value = null;
      shopData_to_address.value = "";
      shopSelectValues_to.value = { shop_code: "", name: "" };
      shopSelectValues_from.value = { shop_code: shopData.value.shop_code, name: shopData.value.name };

      /**
       * 店舗データ取得処理
       */
      const listShopTo = await apolloClient.query<ListShopForTransPickupRequestQuery>({
        query: ListShopForTransPickupRequestDocument,
        variables: {
          input: shopToListInput.value,
        },
      });
      //配達先店舗取得
      shopToListItems.value = uniqueByShopCode(
        listShopTo.data.listShopForTransPickupRequest
      );
      isDisabledChangeFromTo.value = true;
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = err.message;
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "不明なエラーが発生しました。";
      }
      errdialog.value = true;
      return;
    }
  };

  /**
   * 配達先選択時 @change
   */
  const onChangeShopTo = async (): Promise<void> => {
    shopData_to_address.value = "";
    shopData_to_address1.value = "";
    deliveryAreaName.value = "";
    display_delivery_time.value = "";
    deliveryAvailabilityHeader.value = new Map();
    deliveryAvailabilityData.value = [];
    listDeliveryArea.value = [];

    // nullチェックを追加
    if (!shopSelectValues_to.value || shopSelectValues_to.value.shop_code === "") {
      return;
    }

    if (
      shopSelectValues_from.value?.shop_code ===
      shopSelectValues_to.value?.shop_code
    ) {
      shopSelectValues_to.value = { shop_code: "", name: "" };
      errdialog.value = true;
      errMessage.value = "配達元と配達先に異なる店舗を設定してください。";
      return;
    }

    if (
      shopSelectValues_from.value?.shop_code !== localStorageGetShopCode() &&
      shopSelectValues_to.value?.shop_code !== localStorageGetShopCode()
    ) {
      shopSelectValues_to.value = { shop_code: "", name: "" };
      errdialog.value = true;
      errMessage.value = "配達元と配達先のいずれかは自店舗を設定してください。";
      return;
    }

    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      /**
       * 店舗情報取得
       */
      const shop_data = await apolloClient.query<GetShopQuery>({
        query: GetShopDocument,
        variables: {
          chain_code: localStorageGetChainCode(),
          customer_code: localStorageGetCustomerCode(),
          shop_code: shopSelectValues_to.value.shop_code,
        },
      });
      shopData.value = shop_data.data.getShop!;
      shopData_to_address.value =
        "〒" +
        shopData.value.zip_code +
        " " +
        shopData.value.address1 +
        shopData.value.address2 +
        " " +
        (shopData.value.address3 ?? "");
      shopData_to_address1.value = shopData.value.address1;
      shopSelectValues_to.value = { shop_code: shopData.value.shop_code, name: shopData.value.name };
      //配達エリア取得
      await getAreaList(shopData.value.address1);
      isDisabledChangeFromTo.value = false;
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = err.message;
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "不明なエラーが発生しました。";
      }
      errdialog.value = true;
      return;
    }
  };

  /**
   * 配達店舗入れ替えボタンクリック @click
   */
  const onClickShopChangeBtn = async (): Promise<void> => {
    //配達店舗入れ替え
    const befor_Select_from = shopSelectValues_from.value;
    const befor_Address_from = shopData_from_address.value;

    shopSelectValues_from.value = shopSelectValues_to.value;
    shopData_from_address.value = shopData_to_address.value;
    // 配送希望選択の表示を初期化
    deliveryAreaName.value = "";
    display_delivery_time.value = "";
    if (editDeliveryRequest.value) {
      editDeliveryRequest.value.area_code = "";
      editDeliveryRequest.value.required_delivery_date = "";
      editDeliveryRequest.value.delivery_id = "";
    }
    await onChangeShopFrom();
    shopSelectValues_to.value = befor_Select_from;
    shopData_to_address.value = befor_Address_from;

    await onChangeShopTo();
  };

  return {
    onChangeShopFrom,
    onChangeShopTo,
    onClickShopChangeBtn,
  };
};
